import {
  useCreateEarMutation,
  useDeleteEarMutation,
  useUpdateEarMutation,
} from "api/memberPortal/ear/ear.api";
import { EarFileData, EarFormFields } from "api/memberPortal/ear/ear.types";
import {
  useCreateProductFeatureMutation,
  useDeleteProductFeatureMutation,
} from "api/memberPortal/features/productFeatures/productFeatures.api";
import {
  useRemoveFileMutation,
  useUploadFileMutation,
} from "api/memberPortal/files/files.api";
import {
  useProductCreateAsFormDataMutation,
  useProductUpdateAsFormDataMutation,
} from "api/memberPortal/project/product/product.api";
import { useAuth } from "hooks/useAuth";
import { getISOCurrentDate } from "utils/timeUtils";

import { FileMetadata } from "../Schemas/ProductForm.schema";

import type { FeatureData } from "../Schemas/SubSchemas/TestedFeatures.schema";

type EarData = {
  earid?: number;
  apriori?: boolean;
  earFiles: FileMetadata[];
};

type ProductFormValue = {
  ears: {
    earData?: EarData[];
    ear_required?: boolean;
    no_ear_justification?: string;
  };
};

export const useProductMutations = () => {
  const { currentUser } = useAuth();
  const [triggerCreateProduct] = useProductCreateAsFormDataMutation();
  const [triggerUpdateProduct] = useProductUpdateAsFormDataMutation();
  const [triggerCreateEar] = useCreateEarMutation();
  const [triggerUpdateEar] = useUpdateEarMutation();
  const [triggerDeleteEar] = useDeleteEarMutation();
  const [triggerFileUpload] = useUploadFileMutation();
  const [triggerRemoveFile] = useRemoveFileMutation();
  const [triggerCreateProductFeature] = useCreateProductFeatureMutation();
  const [triggerDeleteProductFeature] = useDeleteProductFeatureMutation();

  const handleRemoveEar = async (
    originalEarIds: number[],
    currentEarData: EarData[],
  ) => {
    const finalEars = currentEarData.map((o) => o.earid?.toString() ?? "");
    const toDeleteEars = originalEarIds.filter(
      (orig) => !finalEars.includes(orig.toString()),
    );

    await Promise.all(
      toDeleteEars.map((ear) => triggerDeleteEar({ earid: ear })),
    );
  };

  const handleEarUpload = async (
    value: ProductFormValue,
    productId: number,
  ) => {
    const earData = value.ears.earData ?? [];
    const earRequired = value.ears.ear_required ?? false;
    const noEarJustification = value.ears.no_ear_justification ?? "";
    earData.forEach(async (ear) => {
      if (ear.earid) {
        triggerUpdateEar({
          earid: ear.earid,
          pid: productId,
          ...ear,
        })
          .unwrap()
          .then((data) => {
            handleFileUpload("ear-review", data.earid, ear.earFiles);
          })
          .catch((error) => {
            console.error("Error updating ear", error);
          });
      } else {
        const earFile: EarFileData = {
          file_type: "ear-review",
          file_source_type: "Check In",
          file_display_name: "Entropy Assessment Report (EAR)",
          uploaded_on: getISOCurrentDate(),
          file_name: "",
          file_label: "",
          active: true,
          file: undefined,
          private: true,
        };

        const earFormFields: EarFormFields = {
          apriori: ear.apriori ?? false,
          reqd: earRequired,
          blurba: "",
          just: noEarJustification,
          ear_file_label: "",
          tier3: false,
        };

        triggerCreateEar({
          ear: earFormFields,
          earFile,
          currentUser,
          productId: productId.toString(),
        })
          .unwrap()
          .then((data) => {
            handleFileUpload("ear-review", data.earid, ear.earFiles);
          })
          .catch((error) => {
            console.error("Error creating ear", error);
          });
      }
    });
  };

  const handleFileUpload = (
    fileType: string,
    fileTypeId: number,
    files: FileMetadata[] | undefined,
  ) => {
    if (files && files.length > 0) {
      for (const file of files) {
        if (file.file_id && file.active === false) {
          triggerRemoveFile(file.file_id);
        } else if (!file.file_id && file.file) {
          const fileData = new FormData();
          fileData.set("file", file.file);
          fileData.set("file_type_id", fileTypeId.toString());
          fileData.set("file_type", fileType);
          fileData.set("file_display_name", file.fileType || "");
          fileData.set("active", String(file.active ?? true));
          fileData.set("file_name", file.fileName || "");
          fileData.set("file_label", file.fileLabel || "");
          fileData.set(
            "private",
            file.documentVisibility === "Public" ? "false" : "true",
          );
          fileData.set("uploaded_on", getISOCurrentDate());
          triggerFileUpload(fileData);
        }
      }
    }
  };

  const handleFeatureChanges = async (
    originalFeatureIds: number[],
    currentFeatures: FeatureData[],
    productId: number,
  ) => {
    // find features to delete (original IDs not in current features)
    const currentFeatureIds = currentFeatures
      .filter((f) => f.id !== undefined)
      .map((f) => f.id as number);

    const toDeleteFeatureIds = originalFeatureIds.filter(
      (origId) => !currentFeatureIds.includes(origId),
    );

    // delete removed features
    await Promise.all(
      toDeleteFeatureIds.map((id) =>
        triggerDeleteProductFeature({ id, product: productId }),
      ),
    );

    // create new features (without an id)
    const newFeatures = currentFeatures.filter((f) => f.id === undefined);
    await Promise.all(
      newFeatures.map((feature) =>
        triggerCreateProductFeature({
          feature_name: feature.feature_name,
          product: productId,
        }),
      ),
    );
  };

  return {
    triggerCreateProduct,
    triggerUpdateProduct,
    handleRemoveEar,
    handleEarUpload,
    handleFileUpload,
    handleFeatureChanges,
  };
};
